import AnimatedIcon from "components/AnimatedIcon"
import EmailInput from "components/EmailInput"
import { graphql, useStaticQuery } from "gatsby"
import ConstantMarquee from "library/ConstantMarquee"
import { ScreenContext } from "library/ScreenContext"
import UniversalImage, { type UniversalImageData } from "library/UniversalImage"
import { fmobile, fresponsive, ftablet } from "library/fullyResponsive"
import useMedia from "library/useMedia"
import React, { useContext } from "react"
import styled, { css } from "styled-components"
import colors from "styles/colors"
import { desktopBreakpoint, tabletBreakpoint } from "styles/media"
import textStyles from "styles/text"
import type { IndustryHero } from "types/aliases"

export const renderTitle = (titleString: string, blueTextList: string[]) => {
	return titleString.split(" ").map((word, index) => {
		const isBlue = blueTextList.includes(word)
		const WordComponent = isBlue ? Blue : "span"
		return (
			<React.Fragment key={`${index}-${word}`}>
				{index > 0 && " "}
				<WordComponent className={isBlue ? "blue-text" : ""}>
					{word}
				</WordComponent>
			</React.Fragment>
		)
	})
}

export default function Hero({ data }: { data: IndustryHero }) {
	const { title, subtitle, background, logoMarquee, image, blueText } = data
	const mutableBlueText = blueText?.filter((t) => typeof t === "string")
	const { mobile, tablet } = useContext(ScreenContext)

	const bgImages: Queries.IndustryBackgroundsQuery = useStaticQuery(graphql`
		query IndustryBackgrounds {
			boxes: file(relativePath: { eq: "industry/BoxesBackground.png" }) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			tape: file(relativePath: { eq: "industry/TapeBackground.png" }) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			pallets: file(relativePath: { eq: "industry/PalletBackground.png" }) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			boxesTablet: file(
				relativePath: { eq: "industry/BoxesBackgroundTablet.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			tapeTablet: file(
				relativePath: { eq: "industry/TapeBackgroundTablet.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			palletsTablet: file(
				relativePath: { eq: "industry/PalletBackgroundTablet.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			boxesMobile: file(
				relativePath: { eq: "industry/BoxesBackgroundMobile.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			tapeMobile: file(
				relativePath: { eq: "industry/TapeBackgroundMobile.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
			palletsMobile: file(
				relativePath: { eq: "industry/PalletBackgroundMobile.png" }
			) {
				childImageSharp {
					gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
				}
			}
		}
	`)

	const bgMap: Record<string, UniversalImageData> = {
		boxes: useMedia(
			bgImages.boxes,
			bgImages.boxes,
			bgImages.boxesTablet,
			bgImages.boxesMobile,
		),
		tape: useMedia(
			bgImages.tape,
			bgImages.tape,
			bgImages.tapeTablet,
			bgImages.tapeMobile,
		),
		pallets: useMedia(
			bgImages.pallets,
			bgImages.pallets,
			bgImages.palletsTablet,
			bgImages.palletsMobile,
		),
	}

	return (
		<Wrapper>
			<Background
				image={background ? bgMap[background] : undefined}
				alt={`${background} background`}
			/>
			<Inner>
				<ImageWrapper>
					<Image
						image={image?.gatsbyImageData}
						alt={image?.description ?? "Hero Image"}
					/>
				</ImageWrapper>
				<Right>
					<Header>
						<div style={{ marginLeft: "10px" }}>
							{/* The variant will eventually be pulled in from Contentful also. */}
							<AnimatedIcon variant="facilitiesMore" small={mobile} />
						</div>
						<HeaderTitle>
							{title && renderTitle(title, mutableBlueText ?? [])}
						</HeaderTitle>
						<HeaderSubtitle>{subtitle}</HeaderSubtitle>
						<StyledEmailInput buttonText="Book a Demo" />
					</Header>
					{!mobile && !tablet && (
						<Marquee>
							{logoMarquee?.logos?.map((logo) => {
								return (
									<Logo
										src={logo?.url ?? ""}
										alt={logo?.title ?? "Company Logo"}
										key={logo?.id}
									/>
								)
							})}
						</Marquee>
					)}
				</Right>
			</Inner>
		</Wrapper>
	)
}

const Logo = styled.img`
	${fresponsive(css`
		width: 130px;
		height: 55px;
		margin-right: 55px;
	`)}
`

const Wrapper = styled.section`
	display: grid;
	place-items: center;
	width: 100%;
	position: relative;
	z-index: 101;

	${ftablet(css`
		z-index: 0;
	`)}

	${fmobile(css`
		z-index: 0;
	`)}
`

const Background = styled(UniversalImage)`
	position: absolute;
	overflow: visible;

	${fresponsive(css`
		width: ${desktopBreakpoint}px;
		height: 685px;
		top: -2px;
		left: 0;
	`)}

	${ftablet(css`
		width: ${tabletBreakpoint}px;
		height: 1196px;
		top: -18px;
	`)}

  ${fmobile(css`
		width: 375px;
		height: 800px;
		top: 25px;
	`)}

  img {
		overflow: visible;
	}
`

const Inner = styled.div`
	width: 100%;
	display: flex;

	${fresponsive(css`
		height: 685px;
		padding: 102px 40px 0;
		gap: 24px;
	`)}

	${ftablet(css`
		height: auto;
		flex-direction: column-reverse;
		align-items: center;
		padding: 60px 0;
	`)}

  ${fmobile(css`
		height: auto;
		flex-direction: column-reverse;
		align-items: center;
		padding: 0 0 42px;
	`)}
`

const ImageWrapper = styled.div`
	backdrop-filter: blur(6px);
	border: 1px solid ${colors.grey1};
	background: linear-gradient(
		258deg,
		rgb(255 255 255 / 40%) 0%,
		rgb(255 255 255 / 0%) 100%
	);
	z-index: 3;

	${fresponsive(css`
		width: 495px;
		height: 459px;
		padding: 32px;
		border-radius: 20px;
	`)}

	${ftablet(css`
		margin-top: 28px;
	`)}

  ${fmobile(css`
		width: 346.5px;
		height: 321px;
		padding: 22px;
		border-radius: 14px;
		margin-top: 12px;
	`)}
`

const Image = styled(UniversalImage)`
	${fresponsive(css`
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
		border-radius: 12px;
	`)}

	${fmobile(css`
		border-radius: 8px;
	`)}
`

const Right = styled.div`
	display: flex;
	flex-direction: column;
	z-index: 3;

	${fresponsive(css`
		gap: 24px;
		margin-top: 28px;
	`)}
`

const Header = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;

	${fresponsive(css`
		gap: 24px;
		width: 574.5px;
	`)}

	${ftablet(css`
		gap: 36px;
		width: 878px;
		align-items: center;
	`)}

  ${fmobile(css`
		gap: 24px;
		width: 343px;
		align-items: center;
	`)}
`

const HeaderTitle = styled.h1`
	color: ${colors.charcoal};
	${textStyles.h3};

	${fresponsive(css`
		margin: -5px 0 -5px 10px;
	`)}

	${ftablet(css`
		${textStyles.h2};
		margin: -10px 0;
		text-align: center;
	`)}

  ${fmobile(css`
		${textStyles.h4};
		margin: -5px 0;
		text-align: center;
	`)}
`

const HeaderSubtitle = styled.p`
	color: ${colors.grey3};

	${fresponsive(css`
		${textStyles.p2};
		width: 445px;
		margin-left: 10px;
		margin-bottom: -5px;
	`)}

	${ftablet(css`
		${textStyles.p1};
		width: 616px;
		margin: -5px 0;
		text-align: center;
	`)}

  ${fmobile(css`
		${textStyles.p1};
		width: 100%;
		margin: -5px 0;
		text-align: center;
		padding-bottom: 12px;
	`)}
`

const Marquee = styled(ConstantMarquee)`
	${fresponsive(css`
		align-items: center;
		overflow: clip;
		width: 841px;
		height: 101px;

		&::before {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			left: 0;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to left,
				transparent 0%,
				${colors.alpine} 100%
			);
		}

		&::after {
			content: "";
			z-index: 2;
			position: absolute;
			top: 0;
			right: 0;
			width: 50px;
			height: 100%;
			background: linear-gradient(
				to right,
				transparent 0%,
				${colors.alpine} 100%
			);
		}
	`)}
`

const Blue = styled.span`
	color: ${colors.blue2};

	${fresponsive(css`
		${textStyles.h3};
	`)}

	${ftablet(css`
		${textStyles.h2};
	`)}

  ${fmobile(css`
		${textStyles.h4};
	`)}
`

const StyledEmailInput = styled(EmailInput)`
	${fresponsive(css`
		width: 432px;
	`)}

	${ftablet(css`
		width: 500px;
	`)}

	${fmobile(css`
		width: 346.5px;
	`)}
`
